import { ReactNode } from "react";
import { Checkbox } from "@/components/ui/checkbox";
import { meetingTranscriptText } from "@/lib/agent/genieMeetings";
import { GenieMeetingDetail, MeetingNotesParts } from "@/types/agent/genie-meeting";

export interface MeetingNotesDraft {
  includeSummary: boolean;
  summary: string;
  includeActionItems: boolean;
  /** One action item per line. */
  actionItems: string;
  includeTranscript: boolean;
  transcript: string;
}

const TEXTAREA_CLASS =
  "w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-genie-primary bg-white text-base sm:text-sm leading-relaxed resize-y";

/** Summary and action items start ticked; the transcript, where small talk lives, starts unticked. */
export const createNotesDraft = (meeting: GenieMeetingDetail): MeetingNotesDraft => ({
  includeSummary: !!meeting.summary,
  summary: meeting.summary ?? "",
  includeActionItems: meeting.action_items.length > 0,
  actionItems: meeting.action_items.join("\n"),
  includeTranscript: false,
  transcript: meetingTranscriptText(meeting.transcript),
});

export const draftToParts = (draft: MeetingNotesDraft): MeetingNotesParts => ({
  ...(draft.includeSummary && draft.summary.trim() ? { summary: draft.summary.trim() } : {}),
  ...(draft.includeActionItems
    ? {
        actionItems: draft.actionItems
          .split("\n")
          .map((item) => item.replace(/^\s*[-•*]\s*/, "").trim())
          .filter(Boolean),
      }
    : {}),
  ...(draft.includeTranscript && draft.transcript.trim() ? { transcript: draft.transcript.trim() } : {}),
});

export const hasDraftContent = (draft: MeetingNotesDraft): boolean => {
  const parts = draftToParts(draft);
  return !!parts.summary || (parts.actionItems?.length ?? 0) > 0 || !!parts.transcript;
};

interface ReviewPartProps {
  id: string;
  label: string;
  hint: string;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  children: ReactNode;
}

const ReviewPart = ({ id, label, hint, checked, onCheckedChange, children }: ReviewPartProps) => (
  <section className={`rounded-xl border p-5 transition-colors ${checked ? "border-genie-primary/40" : "border-gray-200"}`}>
    <label htmlFor={id} className="flex items-start gap-3 min-h-[44px] cursor-pointer">
      <Checkbox
        id={id}
        checked={checked}
        onCheckedChange={(value) => onCheckedChange(value === true)}
        className="mt-0.5"
      />
      <span className="min-w-0">
        <span className="text-sm font-semibold text-gray-700 block">{label}</span>
        <span className="text-xs text-gray-500 block mt-0.5">{hint}</span>
      </span>
    </label>
    {checked && <div className="mt-3">{children}</div>}
  </section>
);

interface MeetingKnowledgeReviewProps {
  meeting: GenieMeetingDetail;
  genieName: string;
  draft: MeetingNotesDraft;
  onChange: (draft: MeetingNotesDraft) => void;
}

/**
 * The step before "Add to knowledge": pick which parts of the notes go in and
 * edit them, so small talk and internal remarks never reach customers.
 */
export const MeetingKnowledgeReview = ({ meeting, genieName, draft, onChange }: MeetingKnowledgeReviewProps) => {
  const update = (patch: Partial<MeetingNotesDraft>) => onChange({ ...draft, ...patch });

  return (
    <div className="space-y-4">
      <div className="p-[2px] rounded-xl bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8]">
        <div className="rounded-[10px] bg-white p-5">
          <span className="font-semibold text-gray-900">Choose what {genieName} learns</span>
          <p className="text-sm text-gray-600 mt-2">
            {genieName} shares what it knows with your customers. Tick the parts to add and edit out anything that
            should stay internal.
          </p>
        </div>
      </div>

      {meeting.summary && (
        <ReviewPart
          id="meeting-review-summary"
          label="Summary"
          hint="The short write-up of the meeting."
          checked={draft.includeSummary}
          onCheckedChange={(includeSummary) => update({ includeSummary })}
        >
          <textarea
            aria-label="Summary"
            value={draft.summary}
            onChange={(e) => update({ summary: e.target.value })}
            rows={6}
            className={TEXTAREA_CLASS}
          />
        </ReviewPart>
      )}

      {meeting.action_items.length > 0 && (
        <ReviewPart
          id="meeting-review-action-items"
          label="Action items"
          hint="One per line. Delete a line to leave it out."
          checked={draft.includeActionItems}
          onCheckedChange={(includeActionItems) => update({ includeActionItems })}
        >
          <textarea
            aria-label="Action items"
            value={draft.actionItems}
            onChange={(e) => update({ actionItems: e.target.value })}
            rows={Math.min(10, Math.max(3, meeting.action_items.length + 1))}
            className={TEXTAREA_CLASS}
          />
        </ReviewPart>
      )}

      {meeting.transcript.length > 0 && (
        <ReviewPart
          id="meeting-review-transcript"
          label="Transcript"
          hint="Everything that was said, including small talk. Trim it before adding."
          checked={draft.includeTranscript}
          onCheckedChange={(includeTranscript) => update({ includeTranscript })}
        >
          <textarea
            aria-label="Transcript"
            value={draft.transcript}
            onChange={(e) => update({ transcript: e.target.value })}
            rows={14}
            className={TEXTAREA_CLASS}
          />
        </ReviewPart>
      )}
    </div>
  );
};
